import BaseComponent from '../base-component/base-component'
import Sidebar from './sidebar'
import { Nullable } from '../../types/common'
import { checkEnterKey } from '../../utils/utils'
import CarouselDots from '../carousel/carousel-dots'
import Carousel from '../carousel/carousel'

class SidebarBtn extends BaseComponent {
  buttonSelector: string
  button: Nullable<HTMLElement>
  isDraggingInCarousel: boolean
  toggleAfterDraggingInCarousel: boolean
  startPointX: number
  startPointY: number

  constructor() {
    super()

    this.buttonSelector = `[data-sidebar-button]`

    this.button = this.querySelector(this.buttonSelector)
  }

  connectedCallback(): void {
    if (this.button) {
      this.button.addEventListener('click', this.handleButtonClick)
      this.button.addEventListener('keydown', this.handleKeyDown)
      this.button.addEventListener('mousedown', this.handleButtonMouseDown)
      this.button.addEventListener('mousemove', this.handleButtonMouseMove)
      this.button.addEventListener('mouseup', this.handleButtonMouseUp)
    }
  }

  disconnectedCallback(): void {
    if (this.button) {
      this.button.removeEventListener('click', this.handleButtonClick)
      this.button.removeEventListener('keydown', this.handleKeyDown)
      this.button.removeEventListener('mousedown', this.handleButtonMouseDown)
      this.button.removeEventListener('mousemove', this.handleButtonMouseMove)
      this.button.removeEventListener('mouseup', this.handleButtonMouseUp)
    }
  }

  handleButtonClick = (event: MouseEvent): void => {
    event.preventDefault()
  }

  handleButtonMouseDown = (event: MouseEvent): void => {
    event.preventDefault()

    const carouselParent = this.closest<Carousel>('carousel-component')
    const dotsCarouselParent = this.closest<CarouselDots>('carousel-dots')

    this.isDraggingInCarousel = !!(carouselParent || dotsCarouselParent)

    if (this.isDraggingInCarousel) {
      this.setMoveStartPoint(event.clientX, event.clientY)

      this.toggleAfterDraggingInCarousel = true
    } else {
      this.toggle()
    }
  }

  handleButtonMouseMove = (event: MouseEvent): void => {
    if (this.isDraggingInCarousel) {
      const threshold = 24
      const distanceX = Math.abs(this.startPointX - event.clientX)
      const distanceY = Math.abs(this.startPointY - event.clientY)

      const isAboveThanThreshold =
        distanceX > threshold || distanceY > threshold

      if (isAboveThanThreshold) {
        this.toggleAfterDraggingInCarousel = false
      }
    }
  }

  handleButtonMouseUp = (): void => {
    if (this.isDraggingInCarousel) {
      if (this.toggleAfterDraggingInCarousel) {
        this.toggle()
      }

      this.isDraggingInCarousel = false
      this.toggleAfterDraggingInCarousel = false

      this.setMoveStartPoint(0, 0)
    }
  }

  handleKeyDown = (event: KeyboardEvent): void => {
    const isEnterKey = checkEnterKey(event.key || event.keyCode)

    if (isEnterKey) {
      event.preventDefault()

      this.toggle()
    }
  }

  setMoveStartPoint(x: number, y: number): void {
    this.startPointX = x
    this.startPointY = y
  }

  toggle(): void {
    if (this.button) {
      const sidebarId = this.button.dataset.sidebarId || ''
      const sidebar = document.getElementById(sidebarId) as Sidebar

      if (sidebar) {
        sidebar.toggle()

        this.update()
      }
    }
  }

  update(): void {
    if (this.button) {
      const sidebarId = this.button.dataset.sidebarId || ''
      const sidebar = document.getElementById(sidebarId) as Sidebar

      if (sidebar) {
        this.button.setAttribute(
          'data-aria-expanded',
          sidebar.isOpen ? 'true' : 'false',
        )
      }
    }
  }
}

export default SidebarBtn
